/**
 * The React half of immersive mode: the call's lifecycle, and the one piece of
 * UI iPhone needs.
 *
 * `immersive.ts` holds the browser facts and the imperative entry point.
 * Nothing here decides what a platform can do — it only sequences it.
 */

import * as React from 'react'
import { Share, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  canFullscreen,
  createWakeLock,
  enterFullscreenForCall,
  exitFullscreenIfOwned,
  installIsOnlyRouteToImmersive,
  isFullscreen,
} from '@/lib/immersive'

/**
 * Fullscreen as a control the user operates, rather than something that happens
 * to them.
 *
 * Joining a call used to request the screen automatically. That was removed
 * (operator decision, 2026-08-09): taking over a desktop without being asked is
 * hostile, and on iPhone it was always a no-op anyway because WebKit has no
 * element fullscreen. What is left is this — a button.
 *
 * `available` is false on iPhone, and the caller must render nothing rather
 * than a disabled control: a permanently dead button on the one platform that
 * most wants the effect reads as broken software. `AddToHomeScreenHint` is that
 * platform's answer and says so in words.
 *
 * The `fullscreenchange` subscription is not optional. The user can leave
 * fullscreen by pressing Escape or with a system gesture, and neither routes
 * through our click handler — without listening, the button would keep offering
 * "Exit full screen" for a window that is already a window.
 */
export function useFullscreenControl(): {
  available: boolean
  active: boolean
  toggle: () => void
} {
  const [active, setActive] = React.useState(() => isFullscreen())
  const available = React.useMemo(() => canFullscreen(), [])

  React.useEffect(() => {
    if (!available) return
    const sync = () => setActive(isFullscreen())
    // Both spellings: the unprefixed event, and WebKit's for older Safari.
    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)
    sync()
    return () => {
      document.removeEventListener('fullscreenchange', sync)
      document.removeEventListener('webkitfullscreenchange', sync)
    }
  }, [available])

  const toggle = React.useCallback(() => {
    // Called straight from the click handler with nothing awaited in between,
    // because `requestFullscreen()` spends transient activation and an `await`
    // ahead of it would end the gesture and guarantee a rejection.
    if (isFullscreen()) exitFullscreenIfOwned()
    else enterFullscreenForCall()
  }, [])

  return { available, active, toggle }
}

/* -------------------------------------------------------------------------- */
/* The call's immersive lifecycle                                              */
/* -------------------------------------------------------------------------- */

/**
 * Hold the screen for as long as `active` is true, and give everything back
 * the moment it stops being true.
 *
 * **Entering fullscreen is NOT here**, and that asymmetry is the whole point:
 * `requestFullscreen()` needs transient activation, an effect runs after the
 * join request has been awaited, and the first `await` ends the activation.
 * Entry therefore happens in the join tap handler via
 * `enterFullscreenForCall()`. See the module header of `immersive.ts`.
 *
 * Exit is the opposite: it must happen on paths where no gesture exists at all
 * — the room closed, the token was refused, the component unmounted — so it
 * belongs to the lifecycle.
 *
 * The `!active` branch is not redundant with the cleanup. Fullscreen is
 * requested on the tap, BEFORE the join is known to have succeeded, so a join
 * that then 503s never turns `active` true and never runs a cleanup. Without
 * that branch the join door would be left filling the screen with no browser
 * chrome and no obvious way out.
 *
 * Nothing rendered by this app is conditional on being in fullscreen, which is
 * why the user leaving it themselves (Escape, iPad's un-hideable overlay
 * button, Android back) needs no handling beyond `immersive.ts` forgetting it
 * owns the screen: there is no state that can disagree with reality.
 */
export function useImmersiveCall(active: boolean): void {
  React.useEffect(() => {
    if (!active) {
      exitFullscreenIfOwned()
      return
    }

    const wake = createWakeLock()
    wake.start()

    return () => {
      wake.stop()
      exitFullscreenIfOwned()
    }
  }, [active])
}

/* -------------------------------------------------------------------------- */
/* Add to Home Screen                                                          */
/* -------------------------------------------------------------------------- */

const HINT_KEY = 'era.meet.a2hs'

/**
 * Three sightings, then it retires itself.
 *
 * Dismissal alone is not enough. Someone who neither taps the X nor installs
 * would otherwise meet the same banner on every single visit forever, which is
 * how a helpful hint becomes an ad. Three is enough to be noticed twice and
 * ignored once.
 */
const MAX_SIGHTINGS = 3

type HintMemory = { dismissed: boolean; seen: number }

function readHintMemory(): HintMemory {
  try {
    const raw = localStorage.getItem(HINT_KEY)
    if (!raw) return { dismissed: false, seen: 0 }
    const parsed = JSON.parse(raw) as Partial<HintMemory>
    return { dismissed: parsed.dismissed === true, seen: Number(parsed.seen) || 0 }
  } catch {
    // Private mode, or someone else's value under our key. Treat an unreadable
    // memory as "never shown": the cost is one extra hint, and the alternative
    // is suppressing it forever for everyone browsing privately.
    return { dismissed: false, seen: 0 }
  }
}

function writeHintMemory(memory: HintMemory): void {
  try {
    localStorage.setItem(HINT_KEY, JSON.stringify(memory))
  } catch {
    /* private mode: the hint is simply not remembered. It is a hint. */
  }
}

/**
 * Should we teach Add to Home Screen right now?
 *
 * Read once, at mount. Every input is fixed for the life of the page, and
 * re-evaluating would only risk the banner appearing mid-interaction.
 *
 * `installIsOnlyRouteToImmersive()` (in `immersive.ts`) carries the platform
 * half of the question and the reasoning behind it. What is left here is
 * memory: have we already said this enough times?
 *
 * `minHeight` EXISTS BECAUSE A SIGHTING MUST BE A SIGHTING. The floating
 * variant below cannot render on a short viewport — it would land on top of
 * the join button — and that used to be expressed purely as a CSS
 * `[@media(min-height:640px)]:block`. CSS hides the element; it does not tell
 * this hook, so every landscape mount silently burned one of the three
 * sightings on a banner nobody could see. Three rotations and the hint had
 * retired itself without ever being displayed. The gate therefore has to be
 * asked here, in the same place that spends the budget.
 *
 * `matchMedia` rather than `innerHeight` so the question asked in JS is
 * literally the same question CSS would answer, and it is asked once: a banner
 * that pops in when the keyboard closes is worse than one that waits for the
 * next visit.
 */
function useShouldTeachInstall(minHeight?: number): boolean {
  const [show, setShow] = React.useState(false)

  React.useEffect(() => {
    if (!installIsOnlyRouteToImmersive()) return
    if (minHeight !== undefined && !window.matchMedia(`(min-height: ${minHeight}px)`).matches) return

    const memory = readHintMemory()
    if (memory.dismissed || memory.seen >= MAX_SIGHTINGS) return

    // A sighting is a VISIT, not a mount. This component mounts again every
    // time someone comes back to the door — a rejoin after a refused token, a
    // failed join — and counting those would retire the hint in a single
    // frustrating session, which is the session where it is least deserved.
    // It is also mounted on two different screens (the lobby and the join
    // door) which one navigation can show in sequence; that is one sighting,
    // not two. The same flag absorbs StrictMode's double-invoked effects in dev.
    if (!countedThisVisit) {
      countedThisVisit = true
      writeHintMemory({ ...memory, seen: memory.seen + 1 })
    }
    setShow(true)
  }, [minHeight])

  return show
}

let countedThisVisit = false

/**
 * The only honest "make this full screen" affordance an iPhone has.
 *
 * It is instructions, not a button — **deliberately**. iOS has no
 * `beforeinstallprompt` and no scriptable install, so a control that looked
 * like it would install the app could only ever open a dialog explaining that
 * it cannot. Teaching the two real taps is the whole feature.
 *
 * ==========================================================================
 * WHERE THIS IS MOUNTED, WHICH IS THE PART THAT WAS WRONG
 * ==========================================================================
 *
 * Every predicate in this file was correct and it still showed to nobody,
 * because for eight weeks its only mount was `phase.kind === 'door'` in
 * `routes/call.tsx`. Measured on the deployed site with an iPhone Safari UA:
 *
 *  - lobby → "Create the room" → `/call/E26FFG` : `era.meet.a2hs` is still
 *    `null` afterwards. The effect never ran, so the hint had not merely been
 *    hidden — it had never been considered.
 *  - a cold `/call/7BMYS9` with no stored grant : the hint renders, correctly.
 *
 * The door is skipped by BOTH lobby paths. `HomeRoute.enter()` calls
 * `stashGrant()` *before* `navigate()`, so `CallRoute`'s lazy state initialiser
 * finds a grant and opens straight at `{kind:'live'}`. The only person who ever
 * saw the door was someone opening a shared link in a browser with no stored
 * grant for that code — and the operator, who reported "I don't see it
 * fullscreen when I start a call", is by definition never that person.
 *
 * The lesson generalises past this app: a render condition can be perfectly
 * satisfiable and still be unreachable, and no amount of testing the PREDICATE
 * finds that. Only walking the real journey does.
 *
 * So it now mounts in two places, with the lobby carrying the weight:
 *
 *  - **the lobby** (`inline`), under the button that starts a call. This is
 *    where it belongs. It is the one screen every path goes through, creator
 *    and code-joiner alike; it is the moment before the call rather than
 *    during it; and acting on the advice means leaving the page for Safari's
 *    Share sheet, which is free here and would drop a live call anywhere else.
 *  - **the join door** (`floating`), for the link recipient who never touches
 *    the lobby. Same sentence, positioned over a screen this file does not own.
 *
 * Never during a call. An install banner over live video is the exact thing
 * this feature exists to remove, and the advice is unusable there anyway.
 */
export function AddToHomeScreenHint({
  variant = 'floating',
  className,
}: {
  /**
   * `floating` overlays a screen whose layout we do not own (the join door).
   * `inline` participates in the flow of one we do (the lobby).
   *
   * Not one component with a positioning prop by accident: the two differ in
   * more than position. Floating has to be height-gated and dark-on-dark
   * because the door is a dark full-bleed surface; inline has to use the app's
   * theme tokens and must cost the layout as little as possible.
   */
  variant?: 'floating' | 'inline'
  className?: string
}) {
  // WHY THE HEIGHT GATE IS ONLY ON THE FLOATING VARIANT. The door
  // (`features/call/prejoin.tsx`) is a single centred column on a phone in
  // portrait and the bottom-most thing in it is the Join button; below ~640px
  // of viewport height we are in landscape — 844x390 on an iPhone — where that
  // door switches to two columns and spends nearly all of its height, and a
  // floating banner would sit on the button. The number excludes landscape
  // phones, which is why it is a height query and not a breakpoint. The inline
  // variant has no such problem: it is in the flow, so at 844px wide the lobby
  // is already in its `sm` two-column layout and the page simply scrolls.
  const shouldTeach = useShouldTeachInstall(variant === 'floating' ? 640 : undefined)
  const [open, setOpen] = React.useState(true)

  if (!shouldTeach || !open) return null

  const dismiss = () => {
    setOpen(false)
    writeHintMemory({ ...readHintMemory(), dismissed: true })
  }

  const floating = variant === 'floating'

  return (
    <div
      className={cn(
        floating
          ? cn(
              // `--sab` keeps it clear of the home indicator, where a tap is as
              // likely to switch apps as to hit the dismiss button; `--sal`/
              // `--sar` matter in landscape even though we do not render there.
              'fixed z-50 mx-auto max-w-sm',
              'bottom-[calc(0.75rem+var(--sab))]',
              'left-[calc(0.75rem+var(--sal))] right-[calc(0.75rem+var(--sar))]',
            )
          : // Inline costs the lobby real height on the tightest phone in the
            // range, so it is written to cost as little as possible: one
            // paragraph, no heading, no second card. Its gap from whatever sits
            // above comes from the parent's own rhythm rather than a margin
            // here, so it cannot double up. Measured at 375x667 in FINDINGS.
            undefined,
        className,
      )}
    >
      <div
        className={cn(
          'flex items-start gap-2.5 rounded-xl p-2.5',
          floating
            ? 'gap-3 rounded-2xl border border-white/12 bg-white/10 p-3 pl-3.5 text-[#f3eee8] backdrop-blur-md'
            : 'border border-accent-line/60 bg-accent-soft/40',
        )}
      >
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-[0.75rem] leading-snug',
              floating ? 'text-white/70' : 'text-ink-muted',
            )}
          >
            {/* ONE SENTENCE, and it has to do three jobs: set the expectation
                (this will not go full screen the way FaceTime does), give the
                reason without a lecture about WebKit, and name the two taps.

                "Safari cannot" and not "iPhones cannot": the constraint is the
                browser, and the fix is to stop being in the browser — which is
                exactly what Add to Home Screen does. Saying it that way makes
                the instruction follow from the reason instead of being an
                unrelated trick.

                The glyph is part of the instruction. "Share" is a word people
                have to translate into an icon, and iOS moved that button
                between the top and the bottom of the window in iOS 15, so
                naming a position would be wrong for half of them. */}
            <span className={floating ? 'font-medium text-white' : 'font-medium text-ink'}>
              Safari on iPhone cannot go full screen.
            </span>{' '}
            Tap <Share className="inline size-3.5 -translate-y-px" aria-hidden="true" /> Share, then
            &ldquo;Add to Home Screen&rdquo;, and calls open with no browser bars.
          </p>
        </div>

        {/* 44px, per the rest of this app: this is the control that makes the
            banner go away, and a banner you cannot reliably dismiss is worse
            than no banner. The negative margin keeps the target at 44 without
            making the row 44 tall. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className={cn(
            '-m-1.5 grid size-11 shrink-0 place-items-center rounded-full transition-colors',
            floating
              ? 'text-white/45 active:bg-white/10 active:text-white'
              : 'text-ink-dim active:bg-accent-soft active:text-ink',
          )}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  )
}
